// frontend/src/components/auditor/AuditorTrust.js
// Trust accounts and trust transactions for audit review.

import React, { useState, useEffect, useCallback } from 'react';
import AuditorTable from './AuditorTable';
import { fetchAllPages } from '../../utils/auditorApi';

const SUB_TABS = [
  { id: 'accounts', label: 'Trust Accounts' },
  { id: 'transactions', label: 'Transactions' },
];

const fmt = (v) => v != null ? Number(v).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—';
const fmtDate = (v) => v ? new Date(v).toLocaleDateString() : '—';

export default function AuditorTrust({ authAxios, dateRange }) {
  const [subTab, setSubTab] = useState('accounts');
  const [accounts, setAccounts] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setFetchError(null);
    const [acc, txn] = await Promise.all([
      fetchAllPages(authAxios, 'trust-accounts/'),
      fetchAllPages(authAxios, 'trust-transactions/'),
    ]);
    setAccounts(acc);
    setTransactions(txn);
    if (acc.length === 0 && txn.length === 0) {
      setFetchError('No trust data was returned. Check the browser console for API errors.');
    }
    setLoading(false);
  }, [authAxios, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  // Compute aggregates
  const totalBalance = accounts.reduce((s, a) => s + Number(a.balance || 0), 0);
  const depositTotal = transactions.filter(t => t.transaction_type === 'DEPOSIT').reduce((s, t) => s + Number(t.amount || 0), 0);
  const disbursementTotal = transactions.filter(t => t.transaction_type === 'DISBURSEMENT').reduce((s, t) => s + Number(t.amount || 0), 0);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-800">Trust Accounts</h2>
        <p className="text-sm text-gray-500 mt-1">Client trust accounts and transaction history.</p>
      </div>

      {fetchError && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-4 text-sm flex items-start gap-2">
          <svg className="w-5 h-5 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
          </svg>
          <span>{fetchError}</span>
        </div>
      )}

      {/* Summary cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { label: 'Total Trust Balance', value: `P ${fmt(totalBalance)}`, color: 'text-blue-700 bg-blue-50 border-blue-200' },
          { label: 'Total Deposits', value: `P ${fmt(depositTotal)}`, color: 'text-green-700 bg-green-50 border-green-200' },
          { label: 'Total Disbursements', value: `P ${fmt(disbursementTotal)}`, color: 'text-amber-700 bg-amber-50 border-amber-200' },
        ].map((c, i) => (
          <div key={i} className={`rounded-xl border p-4 ${c.color}`}>
            <p className="text-xs uppercase tracking-wider opacity-70 mb-1">{c.label}</p>
            <p className="text-2xl font-bold">{c.value}</p>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex space-x-1 bg-gray-100 rounded-lg p-1 w-fit">
        {SUB_TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setSubTab(t.id)}
            className={`px-4 py-2 text-sm font-medium rounded-md transition-colors ${
              subTab === t.id ? 'bg-white text-gray-800 shadow-sm' : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {subTab === 'accounts' && (
        <AuditorTable
          title={`Trust Accounts (${accounts.length})`}
          data={accounts}
          columns={[
            { key: 'account_number', label: 'Account #' },
            { key: 'client_name', label: 'Client', render: (v, row) => row.client_name || row.client_details?.name || '—' },
            { key: 'bank_name', label: 'Bank' },
            { key: 'balance', label: 'Balance (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'status', label: 'Status', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'Active' || v === 'ACTIVE' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'created_at', label: 'Opened', render: (v) => fmtDate(v) },
          ]}
          exportCsv={{ filename: 'trust_accounts.csv' }}
          emptyMessage="No trust accounts found."
        />
      )}

      {subTab === 'transactions' && (
        <AuditorTable
          title={`Trust Transactions (${transactions.length})`}
          data={transactions}
          columns={[
            { key: 'reference_number', label: 'Ref #' },
            { key: 'account_number', label: 'Trust Account', render: (v, row) => row.account_number || row.trust_account_details?.account_number || '—' },
            { key: 'transaction_type', label: 'Type', render: (v) => (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                v === 'DEPOSIT' ? 'bg-green-100 text-green-700' :
                v === 'DISBURSEMENT' ? 'bg-red-100 text-red-700' :
                v === 'TRANSFER' ? 'bg-blue-100 text-blue-700' :
                'bg-gray-100 text-gray-600'
              }`}>{v || '—'}</span>
            )},
            { key: 'amount', label: 'Amount (P)', render: (v) => fmt(v), className: 'text-right' },
            { key: 'description', label: 'Description' },
            { key: 'date', label: 'Date', render: (v) => fmtDate(v) },
            { key: 'created_by_username', label: 'By', render: (v, row) => row.created_by_username || row.created_by_details?.username || '—' },
          ]}
          exportCsv={{ filename: 'trust_transactions.csv' }}
          emptyMessage="No trust transactions found."
        />
      )}
    </div>
  );
}
